import type { AdminOwnerOption, EvWorkstationSummary } from "../types/index.ts";

const QUICK_SCOPES = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "due_today", label: "Due Today" },
  { value: "calls_today", label: "Calls Today" },
  { value: "overdue", label: "Overdue" },
  { value: "unassigned", label: "Unassigned" },
  { value: "call_prep_active", label: "Call Prep Active" },
  { value: "ready_for_handoff", label: "Ready For Handoff" },
  { value: "archived", label: "NGMI / Archived" },
] as const;

interface EvWorkflowFiltersProps {
  search: string;
  stage: string;
  priority: string;
  owner: string;
  scope: string;
  scoreBand: string;
  deltaDirection: string;
  summary: EvWorkstationSummary | null;
  owners: AdminOwnerOption[];
  onSearchChange: (value: string) => void;
  onStageChange: (value: string) => void;
  onPriorityChange: (value: string) => void;
  onOwnerChange: (value: string) => void;
  onScopeChange: (value: string) => void;
  onScoreBandChange: (value: string) => void;
  onDeltaDirectionChange: (value: string) => void;
}

export function EvWorkflowFilters({
  search,
  stage,
  priority,
  owner,
  scope,
  scoreBand,
  deltaDirection,
  summary,
  owners,
  onSearchChange,
  onStageChange,
  onPriorityChange,
  onOwnerChange,
  onScopeChange,
  onScoreBandChange,
  onDeltaDirectionChange,
}: EvWorkflowFiltersProps) {
  return (
    <section className="ev-filters" data-ev-tour="workflow-filters">
      <div className="ev-filters__topline" data-ev-tour="table-fallback-brief">
        <span className="section-title">Workstation Queue</span>
        <span className="section-count">{summary ? `${summary.total} total` : "—"}</span>
      </div>

      <div className="ev-filters__controls">
        <input
          type="text"
          className="leaderboard-search"
          data-ev-tour="workflow-filter-search"
          placeholder="Search handle, owner, source, note..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        <select
          className="category-filter"
          data-ev-tour="workflow-filter-stage"
          value={stage}
          onChange={(e) => onStageChange(e.target.value)}
        >
          <option value="all">All Stages</option>
          <option value="lead">Lead</option>
          <option value="group_created">Group Created</option>
          <option value="call_completed">Call Completed</option>
          <option value="passed_ic">Passed IC</option>
          <option value="qualified_for_sale">Qualified For Sale</option>
          <option value="contract_shared">Contract Shared</option>
          <option value="closed_onboarding">Closed / Onboarding</option>
          <option value="ngmi">NGMI</option>
          <option value="no_token">No Token</option>
          <option value="no_for_now">No For Now</option>
        </select>

        <select
          className="category-filter"
          data-ev-tour="workflow-filter-priority"
          value={priority}
          onChange={(e) => onPriorityChange(e.target.value)}
        >
          <option value="all">All Priority</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        <select
          className="category-filter"
          data-ev-tour="workflow-filter-owner"
          value={owner}
          onChange={(e) => onOwnerChange(e.target.value)}
        >
          <option value="all">All Owners</option>
          <option value="unassigned">Unassigned</option>
          {owners
            .filter((user) => user.email)
            .map((user) => (
              <option key={user.uid} value={user.email ?? ""}>
                {user.email}
              </option>
            ))}
        </select>

        <select
          className="category-filter"
          data-ev-tour="workflow-filter-score"
          value={scoreBand}
          onChange={(e) => onScoreBandChange(e.target.value)}
        >
          <option value="all">All Scores</option>
          <option value="90_plus">90+</option>
          <option value="80_to_89">80-89</option>
          <option value="70_to_79">70-79</option>
          <option value="below_70">Below 70</option>
        </select>

        <select
          className="category-filter"
          data-ev-tour="workflow-filter-delta"
          value={deltaDirection}
          onChange={(e) => onDeltaDirectionChange(e.target.value)}
        >
          <option value="all">All Delta</option>
          <option value="up">Trending Up</option>
          <option value="down">Trending Down</option>
          <option value="flat">Flat</option>
        </select>
      </div>

      <div className="ev-filters__scopes" data-ev-tour="workflow-filter-scopes">
        {QUICK_SCOPES.map((item) => (
          <button
            key={item.value}
            type="button"
            className={`view-toggle-btn ${scope === item.value ? "active" : ""}`}
            onClick={() => onScopeChange(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </section>
  );
}
